import {
  BarChart3,
  BedDouble,
  ChartPie,
  HeartHandshake,
  Building2,
  CalendarDays,
  DoorOpen,
  LayoutDashboard,
  Package,
  Receipt,
  ShieldCheck,
  Tags,
  Users,
  Utensils,
} from 'lucide-react';
import type { ModuleCode } from '@horevia/contracts';
import type { LinkProps } from '@tanstack/react-router';
import type { ComponentType } from 'react';

/** Chemin de route typé par l'arbre de routes TanStack. */
export type AppRoutePath = NonNullable<LinkProps['to']>;

/**
 * Modèle de navigation — 8 entrées et sous-entrées reprises du prototype validé
 * (`prototype/assets/app.js`, NAV). Les modules non encore implémentés portent
 * `implemented: false` et affichent la page « Bientôt disponible ».
 *
 * Écart connu : `docs/ux/UX_ARCHITECTURE.md` §5.2 propose 8 entrées **sans**
 * sous-menus ; le prototype validé par Bertrand conserve les sous-entrées.
 * Arbitrage à confirmer (voir rapport de phase).
 */
export interface NavChild {
  key: string;
  /** Clé i18n dans le namespace `nav`. */
  labelKey: string;
  to: AppRoutePath;
  implemented: boolean;
  /** Permission minimale pour afficher l'entrée (aucune = toujours visible). */
  permission?: string;
  /** Une seule de ces permissions suffit (alternative à `permission`). */
  anyPermission?: string[];
  /** Module HOREVIA requis (ADR-014) : l'entrée disparaît si le module est inactif. */
  module?: ModuleCode;
}

export interface NavEntry {
  key: string;
  labelKey: string;
  icon: ComponentType<{ className?: string }>;
  to?: AppRoutePath;
  implemented?: boolean;
  permission?: string;
  /** Une seule de ces permissions suffit (alternative à `permission`). */
  anyPermission?: string[];
  /** Module HOREVIA requis (ADR-014) : l'entrée et ses enfants disparaissent si le module est inactif. */
  module?: ModuleCode;
  children?: NavChild[];
}

/** Vrai si l'utilisateur peut voir l'entrée de navigation. */
export function isNavVisible(
  item: NavEntry | NavChild,
  session: {
    can: (...codes: string[]) => boolean;
    canAny: (...codes: string[]) => boolean;
    hasModule: (module: ModuleCode) => boolean;
  },
): boolean {
  if (item.module && !session.hasModule(item.module)) return false;
  if (item.anyPermission && item.anyPermission.length > 0)
    return session.canAny(...item.anyPermission);
  return !item.permission || session.can(item.permission);
}

/** Préfixes de routes réservés à un module (garde de navigation, ADR-014). */
export const ROUTE_MODULES: ReadonlyArray<{ prefix: string; module: ModuleCode }> = [
  { prefix: '/reservations', module: 'hotel' },
  { prefix: '/calendar', module: 'hotel' },
  { prefix: '/resources', module: 'hotel' },
  { prefix: '/today', module: 'hotel' },
  // Le comptoir (notes ouvertes, ADR-019) est réservé au module Bar : avant le préfixe `/pos`.
  { prefix: '/pos/tabs', module: 'bar' },
  { prefix: '/pos', module: 'pos' },
  { prefix: '/restaurant', module: 'restaurant' },
  { prefix: '/stock', module: 'inventory' },
  // Horevia Analytics (ADR-024) : analyse transverse, module `analytics`.
  { prefix: '/analytics', module: 'analytics' },
  // Horevia CRM (ADR-025) : connaître et fidéliser, module `crm` ; la fiche client reste commune.
  { prefix: '/crm', module: 'crm' },
];

export function moduleForPath(pathname: string): ModuleCode | null {
  const match = ROUTE_MODULES.find(
    (entry) => pathname === entry.prefix || pathname.startsWith(`${entry.prefix}/`),
  );
  return match?.module ?? null;
}

export const NAV: NavEntry[] = [
  {
    key: 'dashboard',
    labelKey: 'nav:items.dashboard',
    icon: LayoutDashboard,
    to: '/',
    implemented: true,
  },
  {
    key: 'reservations',
    labelKey: 'nav:items.reservations',
    icon: CalendarDays,
    module: 'hotel',
    permission: 'reservation.read',
    children: [
      {
        key: 'calendar',
        labelKey: 'nav:items.reservationsCalendar',
        to: '/calendar',
        implemented: true,
        permission: 'availability.read',
      },
      {
        key: 'list',
        labelKey: 'nav:items.reservationsList',
        to: '/reservations',
        implemented: true,
      },
      {
        key: 'new',
        labelKey: 'nav:items.reservationsNew',
        to: '/reservations/new',
        implemented: true,
        permission: 'reservation.create',
      },
    ],
  },
  {
    key: 'customers',
    labelKey: 'nav:items.customers',
    icon: Users,
    permission: 'customer.read',
    children: [
      {
        key: 'people',
        labelKey: 'nav:items.customersPeople',
        to: '/customers/people',
        implemented: true,
      },
      {
        key: 'companies',
        labelKey: 'nav:items.customersCompanies',
        to: '/customers/companies',
        implemented: true,
      },
    ],
  },
  {
    key: 'resources',
    labelKey: 'nav:items.resources',
    icon: BedDouble,
    module: 'hotel',
    permission: 'resource.read',
    children: [
      { key: 'list', labelKey: 'nav:items.resourcesList', to: '/resources', implemented: true },
      {
        key: 'availability',
        labelKey: 'nav:items.resourcesAvailability',
        to: '/resources/availability',
        implemented: true,
        permission: 'availability.read',
      },
      {
        key: 'maintenance',
        labelKey: 'nav:items.resourcesMaintenance',
        to: '/resources/maintenance',
        implemented: true,
        permission: 'availability.read',
      },
    ],
  },
  {
    key: 'operations',
    labelKey: 'nav:items.operations',
    icon: DoorOpen,
    module: 'hotel',
    // Chaque sous-entrée porte sa propre permission : l'agent de ménage
    // (permission `housekeeping.read` seule) doit voir « Ménage ».
    children: [
      {
        key: 'arrivals',
        labelKey: 'nav:items.operationsArrivals',
        to: '/today/arrivals',
        implemented: true,
        permission: 'reservation.read',
      },
      {
        key: 'departures',
        labelKey: 'nav:items.operationsDepartures',
        to: '/today/departures',
        implemented: true,
        permission: 'reservation.read',
      },
      {
        key: 'housekeeping',
        labelKey: 'nav:items.operationsHousekeeping',
        to: '/today/housekeeping',
        implemented: true,
        permission: 'housekeeping.read',
      },
      {
        key: 'tasks',
        labelKey: 'nav:items.operationsTasks',
        to: '/tasks',
        implemented: true,
      },
    ],
  },
  {
    key: 'restaurant',
    labelKey: 'nav:items.restaurant',
    icon: Utensils,
    module: 'pos',
    // Chaque sous-entrée porte sa permission : la cuisine ne voit que « Cuisine ».
    anyPermission: ['order.read', 'catalog.read', 'table.read', 'outlet.read'],
    children: [
      {
        key: 'pos',
        labelKey: 'nav:items.restaurantPos',
        to: '/pos',
        implemented: true,
        permission: 'order.create',
      },
      {
        key: 'orders',
        labelKey: 'nav:items.restaurantOrders',
        to: '/pos/orders',
        implemented: true,
        permission: 'order.read',
      },
      {
        key: 'tabs',
        labelKey: 'nav:items.restaurantTabs',
        to: '/pos/tabs',
        implemented: true,
        permission: 'order.read',
        module: 'bar',
      },
      {
        key: 'kitchen',
        labelKey: 'nav:items.restaurantKitchen',
        to: '/pos/kitchen',
        implemented: true,
        permission: 'order.read',
      },
      {
        key: 'tables',
        labelKey: 'nav:items.restaurantTables',
        to: '/restaurant/tables',
        implemented: true,
        permission: 'table.read',
        module: 'restaurant',
      },
      {
        key: 'catalog',
        labelKey: 'nav:items.restaurantCatalog',
        to: '/pos/catalog',
        implemented: true,
        permission: 'catalog.read',
      },
      {
        key: 'outlets',
        labelKey: 'nav:items.restaurantOutlets',
        to: '/pos/settings',
        implemented: true,
        permission: 'outlet.read',
      },
    ],
  },
  {
    key: 'stock',
    labelKey: 'nav:items.stock',
    icon: Package,
    module: 'inventory',
    permission: 'stock.read',
    children: [
      {
        key: 'levels',
        labelKey: 'nav:items.stockLevels',
        to: '/stock',
        implemented: true,
        permission: 'stock.read',
      },
      {
        key: 'movements',
        labelKey: 'nav:items.stockMovements',
        to: '/stock/movements',
        implemented: true,
        permission: 'stock.read',
      },
      {
        key: 'purchases',
        labelKey: 'nav:items.stockPurchases',
        to: '/stock/purchases',
        implemented: true,
        permission: 'stock.read',
      },
      {
        key: 'counts',
        labelKey: 'nav:items.stockCounts',
        to: '/stock/counts',
        implemented: true,
        permission: 'stock.read',
      },
      {
        key: 'items',
        labelKey: 'nav:items.stockItems',
        to: '/stock/items',
        implemented: true,
        permission: 'stock.read',
      },
    ],
  },
  {
    key: 'billing',
    labelKey: 'nav:items.billing',
    icon: Receipt,
    // « Dépenses » (sprint 2.9) doit rester visible avec la seule permission
    // `expense.read` : le groupe s'ouvre dès qu'une de ses entrées l'est.
    anyPermission: ['invoice.read', 'expense.read'],
    children: [
      {
        key: 'proformas',
        labelKey: 'nav:items.billingProformas',
        to: '/billing/proformas',
        implemented: true,
        permission: 'proforma.read',
      },
      {
        key: 'invoices',
        labelKey: 'nav:items.billingInvoices',
        to: '/billing/invoices',
        implemented: true,
        permission: 'invoice.read',
      },
      {
        key: 'payments',
        labelKey: 'nav:items.billingPayments',
        to: '/billing/payments',
        implemented: true,
        permission: 'payment.read',
      },
      {
        key: 'cash',
        labelKey: 'nav:items.billingCash',
        to: '/billing/cash',
        implemented: true,
        permission: 'cash.read',
      },
      {
        key: 'expenses',
        labelKey: 'nav:items.billingExpenses',
        to: '/finance/expenses',
        implemented: true,
        permission: 'expense.read',
      },
    ],
  },
  {
    key: 'analytics',
    labelKey: 'nav:items.analytics',
    icon: ChartPie,
    module: 'analytics',
    // Même porte que les rapports : `report.operational` ouvre `/analytics/*`.
    permission: 'report.operational',
    children: [
      {
        key: 'overview',
        labelKey: 'nav:items.analyticsOverview',
        to: '/analytics',
        implemented: true,
      },
      {
        key: 'sales',
        labelKey: 'nav:items.analyticsSales',
        to: '/analytics/sales',
        implemented: true,
        module: 'pos',
      },
      {
        key: 'stock',
        labelKey: 'nav:items.analyticsStock',
        to: '/analytics/stock',
        implemented: true,
        module: 'inventory',
        permission: 'report.financial',
      },
    ],
  },
  {
    key: 'crm',
    labelKey: 'nav:items.crm',
    icon: HeartHandshake,
    module: 'crm',
    permission: 'crm.read',
    children: [
      { key: 'overview', labelKey: 'nav:items.crmOverview', to: '/crm', implemented: true },
      {
        key: 'customers',
        labelKey: 'nav:items.crmCustomers',
        to: '/crm/customers',
        implemented: true,
      },
      {
        key: 'followUps',
        labelKey: 'nav:items.crmFollowUps',
        to: '/crm/follow-ups',
        implemented: true,
      },
      {
        key: 'segments',
        labelKey: 'nav:items.crmSegments',
        to: '/crm/segments',
        implemented: true,
      },
      { key: 'loyalty', labelKey: 'nav:items.crmLoyalty', to: '/crm/loyalty', implemented: true },
      {
        key: 'duplicates',
        labelKey: 'nav:items.crmDuplicates',
        to: '/crm/duplicates',
        implemented: true,
        permission: 'customer.merge',
      },
      {
        key: 'settings',
        labelKey: 'nav:items.crmSettings',
        to: '/crm/settings',
        implemented: true,
        permission: 'crm.manage',
      },
    ],
  },
  {
    key: 'reports',
    labelKey: 'nav:items.reports',
    icon: BarChart3,
    to: '/reports',
    implemented: true,
    // `GET /reports/:kind` exige `report.operational` pour tous les rapports
    // (`report.financial` s'y ajoute pour les rapports financiers) : une entrée
    // ouverte avec la seule permission financière mènerait à un 403.
    permission: 'report.operational',
  },
  {
    key: 'administration',
    labelKey: 'nav:items.administration',
    icon: ShieldCheck,
    permission: 'user.read',
    children: [
      {
        key: 'users',
        labelKey: 'nav:items.adminUsers',
        to: '/admin/users',
        implemented: true,
        permission: 'user.read',
      },
      {
        key: 'roles',
        labelKey: 'nav:items.adminRoles',
        to: '/admin/roles',
        implemented: true,
        permission: 'role.read',
      },
      {
        key: 'taxes',
        labelKey: 'nav:items.adminTaxes',
        to: '/admin/taxes',
        implemented: true,
        permission: 'tax.read',
      },
      {
        key: 'settings',
        labelKey: 'nav:items.adminSettings',
        to: '/admin/settings',
        implemented: true,
        permission: 'tenant.read',
      },
      {
        key: 'audit',
        labelKey: 'nav:items.adminAudit',
        to: '/admin/audit',
        implemented: true,
        permission: 'audit.read',
      },
      {
        key: 'support',
        labelKey: 'nav:items.adminSupport',
        to: '/admin/support',
        implemented: true,
        permission: 'support.read',
      },
      {
        key: 'setup',
        labelKey: 'nav:items.adminSetup',
        to: '/setup',
        implemented: true,
        permission: 'tenant.read',
      },
    ],
  },
];

/** Retourne la clé de l'entrée (et sous-entrée) active pour un chemin donné. */
/**
 * Navigation des opérateurs de la plateforme (ADR-021, D-H35) : console
 * (vue d'ensemble, tenants) et administration de leurs propres comptes. Les
 * entrées métier n'existent pas pour le tenant technique `platform`.
 */
export const PLATFORM_NAV: NavEntry[] = [
  {
    key: 'platform',
    labelKey: 'nav:items.platform',
    icon: LayoutDashboard,
    to: '/platform',
    implemented: true,
    permission: 'platform.read_health',
  },
  {
    key: 'platformTenants',
    labelKey: 'nav:items.platformTenants',
    icon: Building2,
    to: '/platform/tenants',
    implemented: true,
    permission: 'platform.read_health',
  },
  // Horevia Pay — abonnements (ADR-023) : tableau financier et catalogue des tarifs.
  {
    key: 'platformBilling',
    labelKey: 'nav:items.platformBilling',
    icon: Receipt,
    to: '/platform/billing',
    implemented: true,
    permission: 'platform.read_health',
  },
  {
    key: 'platformPricing',
    labelKey: 'nav:items.platformPricing',
    icon: Tags,
    to: '/platform/pricing',
    implemented: true,
    permission: 'platform.read_health',
  },
  {
    key: 'admin',
    labelKey: 'nav:items.administration',
    icon: ShieldCheck,
    anyPermission: ['user.read', 'role.read', 'audit.read', 'settings.read'],
    children: [
      // Identité de l'émetteur des factures d'abonnement (ADR-023, D-H53) : fiche, logo, facturation, taxes.
      {
        key: 'settings',
        labelKey: 'nav:items.adminSettings',
        to: '/admin/settings',
        implemented: true,
        permission: 'settings.read',
      },
      {
        key: 'taxes',
        labelKey: 'nav:items.adminTaxes',
        to: '/admin/taxes',
        implemented: true,
        permission: 'tax.read',
      },
      {
        key: 'users',
        labelKey: 'nav:items.adminUsers',
        to: '/admin/users',
        implemented: true,
        permission: 'user.read',
      },
      {
        key: 'roles',
        labelKey: 'nav:items.adminRoles',
        to: '/admin/roles',
        implemented: true,
        permission: 'role.read',
      },
      {
        key: 'audit',
        labelKey: 'nav:items.adminAudit',
        to: '/admin/audit',
        implemented: true,
        permission: 'audit.read',
      },
    ],
  },
];

/** Navigation applicable à la session : console pour le tenant plateforme, métier sinon. */
export function navFor(isPlatform: boolean): NavEntry[] {
  return isPlatform ? PLATFORM_NAV : NAV;
}

/** Vrai pour les écrans de la console plateforme (`/platform` et ses sous-routes). */
export function isPlatformPath(pathname: string): boolean {
  return pathname === '/platform' || pathname.startsWith('/platform/');
}

/** Écrans qu'un opérateur de la plateforme peut ouvrir en dehors de la console. */
export function isPlatformAllowedPath(pathname: string): boolean {
  return (
    isPlatformPath(pathname) ||
    ['/admin', '/me', '/module-unavailable'].some(
      (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
    )
  );
}

export function activeNavKeys(
  pathname: string,
  nav: NavEntry[] = NAV,
): { top?: string; child?: string } {
  for (const entry of nav) {
    if (entry.to && (entry.to === '/' ? pathname === '/' : pathname.startsWith(entry.to))) {
      return { top: entry.key };
    }
    for (const child of entry.children ?? []) {
      if (pathname === child.to || pathname.startsWith(`${child.to}/`)) {
        return { top: entry.key, child: child.key };
      }
    }
  }
  return {};
}
